import { EVENT_TYPE, Match, STATUS } from "../types/types";
import { getMatchStatusText, getGoals } from "../utils";
import ballLogo from "../assets/icons/ball-logo.png";
import { useAppSelector } from "../hooks";
import { dbCreateEvent } from "../api/api";
import { SxProps } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import EventRow from "./eventRow";

interface Props {
  match: Match;
  editable?: boolean;
}

export const iconStyles: SxProps = {
  fontSize: "40px",
  color: "#3A3A3A",

  ":hover": {
    cursor: "pointer",
  },
};

const cardStyles = {
  width: '20px',
  height: '28px',
  borderRadius: '3px',
  cursor: 'pointer',
  transition: 'opacity 0.2s',
};

const yellowCardStyles = {
  ...cardStyles,
  backgroundColor: '#FFD700',
};

const redCardStyles = {
  ...cardStyles,
  backgroundColor: '#DC143C',
};

function MatchData(props: Props) {
  const { match, editable } = props;
  const isAdmin = useAppSelector((state) => state.app.admin);
  const tournament = useAppSelector((state) => state.tournament.tournament);

  const [homeTeamPlayers, setHomeTeamPlayers] = useState<any[]>([]);
  const [awayTeamPlayers, setAwayTeamPlayers] = useState<any[]>([]);
  const players = useAppSelector((state) => state.players.players);

  const getStatusColor = (status: STATUS) => {
    switch (status) {
      case STATUS.PLAYING:
        return "#2e7d32";
      case STATUS.PENDING:
        return "#ff6b00";
      case STATUS.FINISHED:
        return "white";
      default:
        return "white";
    }
  };

  const addGoalHandler = (teamId?: string) => {
    if (!teamId) return;
    const eventNumber = match.homeTeamEvents.length + match.awayTeamEvents.length;
    dbCreateEvent(match.id, teamId, tournament.id, eventNumber, EVENT_TYPE.GOAL);
  };

  const addCardHandler = (teamId: string | undefined, cardType: EVENT_TYPE.YELLOW_CARD | EVENT_TYPE.RED_CARD) => {
    if (!teamId) return;
    const eventNumber = match.homeTeamEvents.length + match.awayTeamEvents.length;
    dbCreateEvent(match.id, teamId, tournament.id, eventNumber, cardType);
  };

  useEffect(() => {
    getPlayers();
  }, []);

  const getPlayers = async () => {
    setHomeTeamPlayers(
      players
        .filter((p) => (p.team.id) === match.homeTeam?.id)
        .sort((a, b) => parseInt(a.number) - parseInt(b.number))
    );
    setAwayTeamPlayers(
      players
        .filter((p) => (p.team.id) === match.awayTeam?.id)
        .sort((a, b) => parseInt(a.number) - parseInt(b.number))
    );
  };

  const homeGoals = getGoals(match.homeTeamEvents);
  const awayGoals = getGoals(match.awayTeamEvents);
  const totalGoals = homeGoals.length + awayGoals.length;

  return (
    <>
      <div className="match-update-dialog-content">
        <div className="match-update-container">
          <div className="row">
            {isAdmin && match?.status === STATUS.PLAYING && <></>}
          </div>
          <div className="row">
            <div className="column">
              <img
                src={
                  match?.homeTeam?.club?.logo
                    ? match?.homeTeam?.club?.logo
                    : ballLogo
                }
              />
              <p className="team-name">{match?.homeTeam?.teamName}</p>
              {isAdmin && match?.status === STATUS.PLAYING && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <div
                    onClick={() => addCardHandler(match.homeTeam?.id, EVENT_TYPE.YELLOW_CARD)}
                    style={yellowCardStyles}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  />
                  <div
                    onClick={() => addCardHandler(match.homeTeam?.id, EVENT_TYPE.RED_CARD)}
                    style={redCardStyles}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  />
                </div>
              )}
            </div>
            <div className="column">
              <div className="center-column-container">
                {isAdmin && match?.status === STATUS.PLAYING && (
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <AddIcon
                      onClick={() => addGoalHandler(match.homeTeam?.id)}
                      sx={iconStyles}
                    />
                    <AddIcon
                      onClick={() => addGoalHandler(match.awayTeam?.id)}
                      sx={iconStyles}
                    />
                  </div>
                )}
                {match?.status === STATUS.PENDING ? (
                  "VS"
                ) : (
                  <div className="result">
                    <p>{homeGoals.length}</p>
                    <p>-</p>
                    <p>{awayGoals.length}</p>
                  </div>
                )}
                {match && (
                  <div
                    className="status"
                    style={{ color: getStatusColor(match?.status) }}
                  >
                    {getMatchStatusText(match?.status)}
                  </div>
                )}
              </div>
            </div>
            <div className="column">
              <img
                src={
                  match?.awayTeam?.club?.logo
                    ? match?.awayTeam?.club?.logo
                    : ballLogo
                }
              />
              <p className="team-name">{match?.awayTeam?.teamName}</p>
              {isAdmin && match?.status === STATUS.PLAYING && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <div
                    onClick={() => addCardHandler(match.awayTeam?.id, EVENT_TYPE.YELLOW_CARD)}
                    style={yellowCardStyles}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  />
                  <div
                    onClick={() => addCardHandler(match.awayTeam?.id, EVENT_TYPE.RED_CARD)}
                    style={redCardStyles}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div style={{ width: "100%" }}>
              {[...match.homeTeamEvents, ...match.awayTeamEvents]
                .sort((a, b) => a.eventNumber - b.eventNumber)
                .map((event) => (
                  <EventRow
                    options={event.team.id === match.homeTeam?.id ? homeTeamPlayers : awayTeamPlayers}
                    key={event.id}
                    eventData={event}
                    matchData={match}
                    justifyContent={event.team.id === match.homeTeam?.id ? 'flex-start' : 'flex-end'}
                    isAdmin={!!isAdmin}
                    tournamentId={tournament.id}
                    editable={editable}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MatchData;
